/* eslint-disable better-mutation/no-mutation */
import { createApi } from '@reduxjs/toolkit/query/react';
import { isomorphicWebApiBaseQuery } from 'tradera/state/services/utils/webapi-base-query';

import type {
    BuyerBoughtPurchaseOrdersIncludingExtendedLifeApiResponse,
    BuyerBoughtPurchaseOrdersIncludingExtendedLifeApiRequest,
    BoughtPageApiRequest,
} from 'tradera/state/services/types/webapi-mytradera-generated';
import type {
    MarkAsPaidByBuyerApiRequest,
    MarkAsPaidByBuyerApiResponse,
    UnmarkAsPaidByBuyerApiRequest,
    UnmarkAsPaidByBuyerApiResponse,
} from 'tradera/state/services/types/webapi-shopping-generated';
import type {
    SetTransactionVisibilityApiRequest,
    SetTransactionVisibilityApiResponse,
    WebMemberSoldItemBuyerInfoApiResponse,
} from 'tradera/state/services/types/webapi-member-generated';
import { PaidStatuses } from 'tradera/constants/paid-statuses';

export const MyBoughtTagTypes = {
    BoughtPurchaseOrders: 'BoughtPurchaseOrders',
} as const;

export const getMyBoughtItemsCacheKeys: Set<BuyerBoughtPurchaseOrdersIncludingExtendedLifeApiRequest> =
    new Set();

export const myPurchasesApi = createApi({
    reducerPath: 'myPurchasesApi',
    baseQuery: isomorphicWebApiBaseQuery,
    tagTypes: Object.values(MyBoughtTagTypes),
    endpoints: (builder) => ({
        getSoldItemBuyerInfo: builder.query<
            WebMemberSoldItemBuyerInfoApiResponse,
            string
        >({
            query: (purchaseOrderId) =>
                `/api/webapi/member/web/selling/solditem/buyer-info/${purchaseOrderId}`,
        }),
        getMyBoughtItems: builder.query<
            BuyerBoughtPurchaseOrdersIncludingExtendedLifeApiResponse,
            BoughtPageApiRequest
        >({
            query: (query) => {
                getMyBoughtItemsCacheKeys.add(query);
                return {
                    url: '/api/webapi/mytradera/web/bought-page',
                    method: 'POST',
                    body: query,
                };
            },
            providesTags: (_response, _error, request) => [
                {
                    type: MyBoughtTagTypes.BoughtPurchaseOrders,
                    id: JSON.stringify(request.buyerFilters),
                },
            ],
        }),
        markAsPaidByBuyer: builder.mutation<
            MarkAsPaidByBuyerApiResponse,
            MarkAsPaidByBuyerApiRequest
        >({
            query: (payload) => ({
                url: '/api/webapi/shopping/payment/markaspaid',
                method: 'POST',
                body: payload,
            }),
            async onQueryStarted(
                { purchaseOrderId, ...patch },
                { dispatch, queryFulfilled },
            ) {
                const patchResults = [];
                for (const cacheKey of getMyBoughtItemsCacheKeys) {
                    const patchResult = dispatch(
                        myPurchasesApi.util.updateQueryData(
                            'getMyBoughtItems',
                            cacheKey,
                            (draft) => {
                                const purchaseOrder =
                                    draft.purchaseOrdersOneOf?.find(
                                        ({ purchaseOrder }) =>
                                            purchaseOrder?.purchaseOrderId ===
                                            purchaseOrderId,
                                    )?.purchaseOrder;
                                if (purchaseOrder?.state) {
                                    purchaseOrder.state.paidStatus =
                                        PaidStatuses.MARKED_AS_PAID_BY_BUYER;
                                }
                                Object.assign(draft, patch);
                            },
                        ),
                    );
                    patchResults.push(patchResult);
                }
                try {
                    await queryFulfilled;
                } catch {
                    for (const patch of patchResults) {
                        patch.undo();
                    }
                }
            },
        }),
        unmarkAsPaidByBuyer: builder.mutation<
            UnmarkAsPaidByBuyerApiResponse,
            UnmarkAsPaidByBuyerApiRequest
        >({
            query: (payload) => ({
                url: '/api/webapi/shopping/payment/unmarkaspaid',
                method: 'POST',
                body: payload,
            }),
            async onQueryStarted(
                { purchaseOrderId, ...patch },
                { dispatch, queryFulfilled },
            ) {
                const patchResults = [];
                for (const cacheKey of getMyBoughtItemsCacheKeys) {
                    const patchResult = dispatch(
                        myPurchasesApi.util.updateQueryData(
                            'getMyBoughtItems',
                            cacheKey,
                            (draft) => {
                                const purchaseOrder =
                                    draft.purchaseOrdersOneOf?.find(
                                        ({ purchaseOrder }) =>
                                            purchaseOrder?.purchaseOrderId ===
                                            purchaseOrderId,
                                    )?.purchaseOrder;
                                if (purchaseOrder?.state) {
                                    purchaseOrder.state.paidStatus =
                                        PaidStatuses.UNPAID;
                                }
                                Object.assign(draft, patch);
                            },
                        ),
                    );
                    patchResults.push(patchResult);
                }
                try {
                    await queryFulfilled;
                } catch {
                    for (const patch of patchResults) {
                        patch.undo();
                    }
                }
            },
        }),
        setIsHidden: builder.mutation<
            SetTransactionVisibilityApiResponse,
            SetTransactionVisibilityApiRequest
        >({
            query: (payload) => ({
                url: '/api/webapi/member/transaction-visibility',
                method: 'POST',
                body: payload,
            }),
            invalidatesTags: (_response, _error, request) =>
                request.hidden
                    ? // request.hidden here means that we have hidden an item
                      // from a list where we do not show hidden items.
                      // Only invalidate requests that are hidden="True".
                      // This is because the updated visibility might not have
                      // propageted all the way to Elastic yet, so if we invalidate
                      // the request where we show non-hidden items we request an
                      // updated list which still contains the item that the user
                      // just hid. Hiding it in the client is handled by the
                      // onQueryStarted optimistic update
                      [...getMyBoughtItemsCacheKeys.values()]
                          .filter(
                              (cacheKey) =>
                                  cacheKey.buyerFilters?.hidden === 'True',
                          )
                          .map((cacheEntry) => ({
                              type: MyBoughtTagTypes.BoughtPurchaseOrders,
                              id: JSON.stringify(cacheEntry.buyerFilters),
                          }))
                    : // and reversed for lists with hidden items, if we have
                      // un-hidden an item, we should update all lists where it might
                      // show up, i.e lists that contains items that aren't hidden
                      [...getMyBoughtItemsCacheKeys.values()]
                          .filter(
                              (cacheKey) =>
                                  cacheKey.buyerFilters?.hidden === 'False',
                          )
                          .map((cacheEntry) => ({
                              type: MyBoughtTagTypes.BoughtPurchaseOrders,
                              id: JSON.stringify(cacheEntry.buyerFilters),
                          })),
            async onQueryStarted(patch, { dispatch, queryFulfilled }) {
                const patchResults = [];
                for (const cacheKey of getMyBoughtItemsCacheKeys) {
                    if (
                        // When on the hidden tab
                        (cacheKey.buyerFilters?.hidden === 'True' &&
                            !patch.hidden) || // When not on the hidden tab
                        (cacheKey.buyerFilters?.hidden === 'False' &&
                            patch.hidden)
                    ) {
                        const patchResult = dispatch(
                            myPurchasesApi.util.updateQueryData(
                                'getMyBoughtItems',
                                cacheKey,
                                (draft) => {
                                    const updatedItems =
                                        draft.purchaseOrdersOneOf?.filter(
                                            ({
                                                purchaseOrder,
                                                extendedLifePurchaseOrder,
                                            }) =>
                                                purchaseOrder?.purchaseOrderId !==
                                                    patch.purchaseOrderId &&
                                                extendedLifePurchaseOrder?.purchaseOrderId !==
                                                    patch.purchaseOrderId,
                                        );

                                    Object.assign(draft, {
                                        purchaseOrdersOneOf: updatedItems,
                                    });
                                },
                            ),
                        );
                        patchResults.push(patchResult);
                    }
                }

                try {
                    await queryFulfilled;
                } catch {
                    for (const patch of patchResults) {
                        patch.undo();
                    }
                }
            },
        }),
    }),
});

export const {
    useGetMyBoughtItemsQuery,
    useMarkAsPaidByBuyerMutation,
    useUnmarkAsPaidByBuyerMutation,
    useSetIsHiddenMutation,
    useGetSoldItemBuyerInfoQuery,
} = myPurchasesApi;
